/**
 * Categorias (redesign admin, Fase 4): produtos agrupados por
 * `operational.category` (ver `catalog/categories.ts`). Somente leitura — a
 * categoria é cadastro do Bling; cada linha leva à lista de Catálogo filtrada.
 */
import { createElement as h, type ReactElement } from "react";
import { Link } from "react-router-dom";
import { CatalogBoundary } from "../components/CatalogBoundary.js";
import { StatusPanel } from "../components/StatusPanel.js";
import { AWAITING_CONTRACT_LABEL } from "../dashboard/kpis.js";
import type { CatalogDataset } from "../catalog/dataset.js";
import { categorySummaries } from "../catalog/categories.js";
import { formatCount } from "../format.js";
import { Card, EmptyState, PageHeader } from "../ui/index.js";

export function CategoriasView({ catalog }: { catalog: CatalogDataset }): ReactElement {
  const header = h(PageHeader, {
    title: "Categorias",
    description: "Produtos agrupados pela categoria cadastrada no Bling."
  });
  if (catalog.sources.platformCatalog === "awaiting_contract") {
    return h(
      "div",
      { className: "page page--categorias flex flex-col gap-6", "data-page": "categorias" },
      header,
      h(StatusPanel, { kind: "awaiting_source", title: `Catálogo da Plataforma ${AWAITING_CONTRACT_LABEL}` })
    );
  }
  const rows = categorySummaries(catalog.products);
  return h(
    "div",
    { className: "page page--categorias flex flex-col gap-6", "data-page": "categorias" },
    header,
    rows.length === 0
      ? h(EmptyState, { title: "Nenhum produto no catálogo" })
      : h(
          Card,
          null,
          h(
            "table",
            { className: "w-full text-left text-sm", "data-table": "categorias" },
            h(
              "thead",
              null,
              h(
                "tr",
                { className: "text-xs uppercase text-muted" },
                ["Categoria", "Produtos", "Publicados", "Disponível (un.)", "Em risco"].map((title) =>
                  h("th", { key: title, scope: "col", className: "px-2 py-2" }, title)
                )
              )
            ),
            h(
              "tbody",
              null,
              rows.map((row) =>
                h(
                  "tr",
                  { key: row.label, "data-category": row.label, className: "border-t border-border" },
                  h(
                    "td",
                    { className: "px-2 py-2 font-semibold" },
                    row.category
                      ? h(Link, { to: `/catalogo?categoria=${encodeURIComponent(row.category)}`, className: "text-brand-text" }, row.label)
                      : row.label
                  ),
                  h("td", { className: "px-2 py-2 tabular-nums" }, formatCount(row.products)),
                  h("td", { className: "px-2 py-2 tabular-nums" }, formatCount(row.published)),
                  h("td", { className: "px-2 py-2 tabular-nums" }, formatCount(row.availableUnits)),
                  h("td", { className: "px-2 py-2 tabular-nums" }, formatCount(row.atRisk))
                )
              )
            )
          )
        )
  );
}

export function Categorias(): ReactElement {
  return h(CatalogBoundary, { children: (catalog: CatalogDataset) => h(CategoriasView, { catalog }) });
}
